import { GolfAvatar } from '@/components/ui/GolfAvatar';
import { timeAgo } from '@/utils/timeAgo';
import { formatDistanceToNow } from 'date-fns';
import React from 'react';
import { useTranslation } from 'react-i18next';

type ChatListItemProps = {
  chat: {
    id: string;
    name: string;
    imageUrl?: string;
    lastMessage?: {
      text: string;
      timestamp: string;
      sender: string;
    };
    unread?: boolean;
    type?: 'direct' | 'group';
    participants?: string[];
  };
  isActive: boolean;
  onClick: () => void;
  userPresence?: Record<string, { online: boolean, lastSeen?: string }>;
};

export const ChatListItem: React.FC<ChatListItemProps> = ({ chat, isActive, onClick, userPresence }) => {
  const { t } = useTranslation();

  // For direct chats, get the other user's presence status
  const getOtherUserPresence = () => {
    if (chat.type === 'direct' && chat.participants && chat.participants.length === 2 && userPresence) {
      // In a direct chat, find the other user (not the current user)
      const otherUserId = chat.participants[0]; // Simplified - in real app you'd compare with current user
      return userPresence[otherUserId];
    }
    return null;
  };

  const otherUserPresence = getOtherUserPresence();

  return (
    <div
      className={`flex flex-1 cursor-pointer items-center gap-3 rounded-lg p-2 transition-colors ${isActive
        ? 'md:bg-secondary'
        : 'hover:bg-muted/50'
        }`}
      onClick={onClick}
    >
      <div className="relative">
        <GolfAvatar className="size-12 flex-shrink-0" imageUrl={chat.imageUrl} />

        {/* Online status indicator */}
        {chat.type === 'direct' && otherUserPresence && (
          <div
            className={`absolute -bottom-px -right-px size-4 rounded-full border-[3px] border-card ${otherUserPresence.online ? 'bg-green-500' : 'bg-gray-400'}`}
            title={otherUserPresence.online ? t('online') : otherUserPresence.lastSeen ? `Last seen ${formatDistanceToNow(new Date(otherUserPresence.lastSeen))} ago` : t('offline')}
          />
        )}
      </div>

      <div className="flex-1 overflow-hidden">
        <div className="flex items-center justify-between">
          <div className="flex items-center gap-1">
            <h3 className="font-semibold truncate">{chat.name}</h3>
          </div>
          {chat.lastMessage && (
            <span className="text-xs text-muted-foreground">
              {timeAgo(new Date(chat.lastMessage.timestamp), t)}
            </span>
          )}
        </div>

        {chat.lastMessage && (
          <p className={`text-sm truncate ${chat.unread ? 'font-medium' : 'text-muted-foreground'}`}>
            {chat.lastMessage.text}
          </p>
        )}

        {chat.unread && (
          <div className="mt-1 flex justify-end">
            <div className="size-2 rounded-full bg-primary" />
          </div>
        )}
      </div>
    </div>
  );
};
